import queryString from "query-string";
import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { studentSignUpFormResolver } from "../../../../constants/resolvers";
import { ROUTES } from "../../../../constants/routes";
import { invitationCodeSchema } from "../../../../constants/schemas";
import {
  useGetAllGradesQuery,
  useStudentSignUpMutation,
  useValidateInvitationCodeMutation,
  useValidatePhoneMutation
} from "../../../../store/auth/auth.api";
import { useSendOTPWithRecaptcha, useVerifyOTPMutation } from "../../../../store/auth/otp.api";
import { CustomSelector } from "../../../components/CustomSelector";
import { useTimer } from "react-timer-hook";
import { PhoneInput } from "../../../components/form/PhoneInput";
import OtpInput from "react-otp-input";
import loaderGif from "../../../../assets/gifs/LoaderGif.gif";
import { useRecaptchaDismissalDetector } from "../../../../hooks/useRecaptchaDismissalDetector";
import { DEFAULT_COUNTRY } from "../../../../constants/country-config";
import { webengageRegister } from "../../../../webengage/webengageService";

export const StudentSignUpForm = ({
  toggleTAndC,
  togglePAndP,
  defaultInvitationCode,
  isInvitationCodeDisabled = false,
  renderExtraFields,
  customResolver,
  getIsExtraFieldsValid = () => true,
  extraDefaultValues = {},
  transformRegisterPayload,
  customRegisterMutation,
  onRegistrationSuccess,
  onRegistrationError
}) => {
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isTimerCompleted, setIsTimerCompleted] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [loader, setLoader] = useState(false);
  const [otpValue, setOtpValue] = useState(Array(4).fill(""));
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [sendOTP, { isLoading: sendOTPLoading, isUninitialized }] = useSendOTPWithRecaptcha();
  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setTimerExpired(true);
      setIsTimerCompleted(true);
    }
  });
  const [verifyPhoneNumber] = useValidatePhoneMutation();

  // Setup reCAPTCHA dismissal detection
  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setLoader(false);
      setIsButtonDisabled(false);
      setError("phoneNumber", {
        type: "recaptcha",
        message: "Security verification cancelled. Please try again."
      });
    }
  });

  const { CODE = null } = queryString.parse(location.search);
  const initialInvitationCode = defaultInvitationCode || CODE || "";

  const [defaultRegister] = useStudentSignUpMutation();
  const customMutationFn = customRegisterMutation;
  const onRegister = customMutationFn || defaultRegister;

  const [validateInvitationCode, { isUninitialized: validateInvitationCodeIsUninitialized }] =
    useValidateInvitationCodeMutation();
  const [verifyOTP, { isSuccess: verifyOTPIsSuccess }] = useVerifyOTPMutation();
  const { data: gradeList, isLoading: gradeListLoading } = useGetAllGradesQuery();

  const form = useForm({
    resolver: customResolver || studentSignUpFormResolver,
    context: {
      phoneLength: selectedCountry.phoneLength
    },
    defaultValues: {
      schoolCode: 0,
      invitationCode: initialInvitationCode,
      remember: true,
      ...extraDefaultValues
    },
    mode: "onChange"
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    clearErrors,
    trigger,
    setValue,
    getValues
  } = form;
  const { formState } = form;
  const { isSubmitting } = formState;

  const watchedValues = watch();
  const { invitationCode, phoneNumber, firstName, gradeCode, remember } = watchedValues;

  useEffect(() => {
    checkValidCode();
  }, [invitationCode]);

  useEffect(() => {
    if (CODE || defaultInvitationCode) {
      checkValidCode();
    }
  }, []);

  const schoolListParse = useMemo(() => {
    if (!gradeListLoading && gradeList?.length) {
      return gradeList.map((value) => ({
        id: value.grade_id,
        value: value.grade_name
      }));
    }
    return [];
  }, [gradeListLoading, gradeList]);

  const checkValidCode = async () => {
    if (invitationCodeSchema.isValidSync(invitationCode)) {
      const result = await validateInvitationCode(invitationCode);
      await clearErrors("invitationCode");

      if (!result?.data?.valid_code) {
        setError("invitationCode", { message: "Code is not valid" });
      }
    } else if (!validateInvitationCodeIsUninitialized) {
      setError("invitationCode", { message: "Code is not valid" });
    }
  };

  const resetOtpFlow = (keepPhoneDisabled = false) => {
    setIsEditEnabled(keepPhoneDisabled);
    setShowOtpInput(false);
    setIsOtpSent(false);
    setOtpValue(Array(4).fill(""));
    Array(4)
      .fill("")
      .forEach((_, index) => {
        clearErrors(`otpCode[${index}]`);
      });
    clearErrors("otpCode");
    restart();
    setIsTimerCompleted(true);
  };

  const onSubmit = async (data) => {
    try {
      if (verifyOTPIsSuccess) {
        let payload = {
          first_name: data.firstName,
          last_name: data.lastName || "",
          whatsapp_consent: data.remember,
          isd_code: selectedCountry.isdCode,
          phone: data.phoneNumber,
          grade_id: data.gradeCode,
          school_code: data.invitationCode,
          validate_account: true
        };

        // Apply payload transformation if provided
        if (transformRegisterPayload) {
          payload = transformRegisterPayload(payload);
        }
        const res = await onRegister(payload);

        if (res.error) {
          const errorData = res.error?.data || res.error;
          const errorMessage = errorData?.message || errorData?.detail || "Registration failed. Please try again.";

          if (errorMessage.includes("Invalid school_code") || errorMessage.includes("Invalid school")) {
            setError("invitationCode", { type: "custom", message: "Invalid school ID. Please check and try again." });
            if (onRegistrationError) {
              onRegistrationError(errorMessage);
              return;
            }
            setIsDisable(false);
            if (isOtpSent) {
              resetOtpFlow(true);
            }
          } else {
            setError("phoneNumber", { type: "custom", message: errorMessage });
          }
          return;
        }

        if (onRegistrationSuccess) {
          onRegistrationSuccess(res.data);
          const userData = res?.data?.details?.data || res?.data?.data || res?.data;
          webengageRegister(userData);
          return;
        }
      } else {
        setError("otpCode", { type: "custom", message: "Otp code must be verified!" });
      }
    } catch (error) {
      console.error("Submit error:", error);
      setError("phoneNumber", { type: "custom", message: "An error occurred. Please try again." });
    }

    localStorage.setItem("studentFormData", JSON.stringify(data));
  };
  const clickSendOTP = async () => {
    setOtpValue(Array(4).fill(""));
    setError("otpCode", { type: "custom", message: "" });
    setIsButtonDisabled(true);
    setLoader(true);

    if (!phoneNumber || phoneNumber.length !== selectedCountry.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${selectedCountry.phoneLength} digits`
      });
      setIsButtonDisabled(false);
      setLoader(false);
      return false;
    }

    clearErrors("phoneNumber");
    setIsTimerCompleted(false);

    setupDismissalListener();
    try {
      const otpResult = await sendOTP({
        phoneNumber: phoneNumber,
        isdCode: selectedCountry.isdCode
      });

      if (otpResult?.data) {
        const time = new Date();
        time.setSeconds(time.getSeconds() + 30);
        restart(time);
        setIsEditEnabled(true);
        setLoader(false);
        return true;
      } else {
        const errorData = otpResult?.error?.data || otpResult?.error || otpResult;
        const errorMessage = errorData?.message || errorData?.detail || "Failed to send OTP";
        const errorDetail = errorData?.detail || "";

        if (errorDetail.includes("reCAPTCHA")) {
          setError("phoneNumber", {
            type: "recaptcha",
            message: "Security verification failed. Please try again."
          });
        } else {
          setError("phoneNumber", {
            type: "api",
            message: errorMessage
          });
        }
        setLoader(false);
        setIsButtonDisabled(false);
        return false;
      }
    } catch (error) {
      setLoader(false);
      setIsButtonDisabled(false);
      const errorData = error?.error?.data || error?.error || error;
      const errorMessage = errorData?.message || errorData?.detail || "An unexpected error occurred. Please try again.";

      setError("phoneNumber", {
        type: "api",
        message: errorMessage
      });
      return false;
    }
  };

  const handlePhoneNumberChange = async (phoneNumber, country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", phoneNumber);

    if (loader) return;

    if (phoneNumber.length < country.phoneLength && phoneNumber.length > 0) {
      clearErrors("phoneNumber");
      return;
    }

    if (phoneNumber.length > country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      return;
    }

    if (phoneNumber.length === country.phoneLength) {
      clearErrors("phoneNumber");
      const result = await verifyPhoneNumber({
        phoneNumber: phoneNumber,
        isdCode: country.isdCode
      });

      if (result?.data?.isVerified) {
        setError("phoneNumber", { message: "User already exists! Please use a different number." });
      } else {
        clearErrors("phoneNumber");
        setValue("phoneNumberVerified", true);
      }
    }
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", "");
    clearErrors("phoneNumber");
    setIsButtonDisabled(true);

    if (isOtpSent) {
      resetOtpFlow(false);
      setIsDisable(false);
    }
  };

  const handleEditNumberClick = () => {
    setIsDisable(false);
    setIsButtonDisabled(true);
    resetOtpFlow();
  };

  const disabled = useMemo(() => {
    return {
      phoneForm: isRunning || sendOTPLoading || isSubmitting,
      otpForm: !(isRunning || !isUninitialized) || isSubmitting
    };
  }, [isRunning, isUninitialized, sendOTPLoading, isSubmitting]);

  useEffect(() => {
    if (timerExpired && isOtpSent) {
      setTimerExpired(false);
    }
    if (timerExpired && !isOtpSent) {
      setShowOtpInput(false);
      setTimerExpired(false);
    }
    return cleanup;
  }, [timerExpired, isOtpSent, cleanup]);

  const handleSignUp = async () => {
    const otpString = otpValue.join("");
    setIsTimerCompleted(true);

    if (otpString.length !== 4) {
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
      return;
    }

    setValue("otpCode", otpString);
    const result = await verifyOTP({
      otpCode: otpString,
      phoneNumber: phoneNumber,
      isdCode: selectedCountry.isdCode
    });

    if (result?.error?.data?.message?.includes("Incorrect OTP, please try again")) {
      setError("otpCode", { type: "custom", message: "OTP is incorrect or not valid" });
      setIsButtonDisabled(true);
      setLoader(false);
      return;
    }

    if (!result?.data) {
      setError("otpCode", { type: "custom", message: "OTP is incorrect or not valid" });
      setIsButtonDisabled(true);
      setLoader(false);
      return;
    }

    if (result?.data) {
      const formData = getValues();

      if (!formData.firstName) {
        setError("firstName", { type: "custom", message: "First name is required" });
        setLoader(false);
        return;
      }

      if (!formData.gradeCode || formData.gradeCode <= 0) {
        setError("gradeCode", { type: "custom", message: "Please select a grade" });
        setLoader(false);
        return;
      }

      if (!formData.invitationCode) {
        setError("invitationCode", { type: "custom", message: "School ID is required" });
        setLoader(false);
        return;
      }
      console.log(formData);
      let payload = {
        first_name: formData.firstName,
        last_name: formData.lastName || "",
        whatsapp_consent: formData.remember,
        isd_code: selectedCountry.isdCode,
        phone: formData.phoneNumber,
        grade_id: formData.gradeCode,
        school_code: formData.invitationCode,
        targetExam: formData.targetExam,
        validate_account: true
      };

      // Apply payload transformation if provided
      if (transformRegisterPayload) {
        payload = transformRegisterPayload(payload);
      }

      try {
        console.log(payload);
        const res = await onRegister(payload);

        if (res.error) {
          const errorData = res.error?.data || res.error;
          const errorMessage = errorData?.message || errorData?.detail || "Registration failed. Please try again.";
          const errorDetail = errorData?.detail || "";

          if (errorDetail?.includes("Similar users found") || errorMessage?.includes("already exists")) {
            setError("phoneNumber", {
              type: "custom",
              message: "User already exists! Please use a different number to create account."
            });
            handleEditNumberClick();
          } else if (
            errorDetail?.includes("Invalid school_code") ||
            errorDetail?.includes("Invalid school") ||
            errorMessage?.includes("school")
          ) {
            setError("invitationCode", {
              type: "custom",
              message: "Invalid school ID. Please check and try again."
            });
            if (onRegistrationError) {
              onRegistrationError(errorMessage);
            }
            setIsDisable(false);
            if (isOtpSent) {
              resetOtpFlow(true);
            }
          } else if (res.error.status === 422 || errorData?.code === 422) {
            const validationErrors = errorData?.detail || errorData?.errors;

            if (typeof validationErrors === "object") {
              Object.keys(validationErrors).forEach((field) => {
                const errorField =
                  field === "first_name"
                    ? "firstName"
                    : field === "grade_id"
                    ? "gradeCode"
                    : field === "school_code"
                    ? "invitationCode"
                    : field === "phone"
                    ? "phoneNumber"
                    : field;

                const errorMsg = Array.isArray(validationErrors[field])
                  ? validationErrors[field][0]
                  : validationErrors[field];

                setError(errorField, {
                  type: "custom",
                  message: errorMsg || "Invalid input"
                });
              });
            } else {
              setError("phoneNumber", {
                type: "custom",
                message: errorMessage || "Please check all fields and try again."
              });
            }
          } else {
            setError("phoneNumber", {
              type: "custom",
              message: errorMessage
            });
          }

          setLoader(false);
          setIsButtonDisabled(true);
          return;
        }

        // Registration successful
        if (onRegistrationSuccess) {
          onRegistrationSuccess(res.data);
        }

        const userData = res?.data?.details?.data || res?.data?.data || res?.data;
        webengageRegister(userData);
        setLoader(false);
      } catch (error) {
        console.error("Registration error:", error);
        setError("phoneNumber", {
          type: "custom",
          message: "An unexpected error occurred. Please try again."
        });
        setLoader(false);
        setIsButtonDisabled(true);
        return;
      }
    }
  };

  const handleButtonAction = async () => {
    if (!isOtpSent) {
      setIsDisable(true);
      try {
        const success = await clickSendOTP();
        if (success) {
          setIsOtpSent(true);
          setShowOtpInput(true);
          setIsTimerCompleted(false);
          setIsButtonDisabled(true);
        } else {
          setIsDisable(false);
          setIsButtonDisabled(false);
        }
      } catch (error) {
        console.error("Error in send OTP:", error);
        setIsDisable(false);
        setIsButtonDisabled(false);
        setLoader(false);
      }
    } else {
      setLoader(true);
      try {
        await handleSignUp();
      } catch (error) {
        console.error("Error in sign up:", error);
        setLoader(false);
        setIsButtonDisabled(true);
      } finally {
        setLoader(false);
      }
    }
  };

  const onBlur = async (fieldName) => {
    if (fieldName === "phoneNumber") {
      // Show error on blur if length doesn't match
      if (phoneNumber && phoneNumber.length > 0) {
        if (phoneNumber.length !== selectedCountry.phoneLength) {
          setError("phoneNumber", {
            message: `Phone number must be ${selectedCountry.phoneLength} digits`
          });
        } else {
          clearErrors("phoneNumber");
        }
      } else if (!phoneNumber || phoneNumber.length === 0) {
        setError("phoneNumber", {
          message: `Phone number is required`
        });
      }
    } else {
      await trigger(fieldName);
    }
  };

  const handleInputKeyPress = (field) => {
    const { name, value } = field;
    switch (name) {
      case "firstName":
        const nameVal = value.replace(/^\s+/g, "").replace(/[^A-Za-z\s]/gi, "");
        setValue(name, nameVal);
        break;
      case "lastName":
        const lastNameVal = value.replace(/[^A-Za-z\s]/gi, "");
        setValue(name, lastNameVal);
        break;
      case "invitationCode":
        const codeVal = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
        setValue(name, codeVal);
        if (isOtpSent) {
          resetOtpFlow(true);
          setIsDisable(false);
        }
        break;
      default:
        break;
    }
  };

  const handleOtpCodeChange = (e) => {
    if (e.length >= 0 && e.length <= 3) {
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
    } else {
      clearErrors("otpCode");
    }
    setOtpValue([...e]);
  };

  useEffect(() => {
    const additionalValidation = getIsExtraFieldsValid(watchedValues);
    const isPhoneValid = phoneNumber?.length === selectedCountry.phoneLength;
    const isAllFieldsFilled =
      !!firstName && !!invitationCode && isPhoneValid && !!remember && gradeCode > 0 && additionalValidation;

    setIsButtonDisabled(!isAllFieldsFilled || (isOtpSent && otpValue.join("").length !== 4));
  }, [watchedValues, isOtpSent, otpValue, getIsExtraFieldsValid, selectedCountry, phoneNumber]);

  return (
    <form onChange={(e) => handleInputKeyPress(e.target)} onSubmit={handleSubmit(onSubmit)}>
      <div className="row">
        <div className="col-6 pe-2 mb-12">
          <label className="tab-lable">First Name</label>
          <input
            type="text"
            style={{ fontFamily: "Source Code Pro" }}
            className={`member--inputbox ${errors.firstName ? "error--strike" : "input-highlight"}`}
            {...register("firstName")}
            required
            disabled={isDisable}
          />
          {errors.firstName && <span className="error--strike--text">{errors.firstName.message}</span>}
        </div>
        <div className="col-6 ps-2 mb-12">
          <label className="tab-lable">Last Name</label>
          <input
            type="text"
            style={{ fontFamily: "Source Code Pro" }}
            className={`member--inputbox ${errors.lastName ? "error--strike" : ""}`}
            {...register("lastName")}
            disabled={isDisable}
          />
          {errors.lastName && <span className="error--strike--text">{errors.lastName.message}</span>}
        </div>

        <div className="col-12 mb-12">
          <label className="tab-lable">Select your grade</label>
          <CustomSelector
            handleInputKeyPress={(selected) => {
              setValue("gradeCode", selected.id);
            }}
            data={schoolListParse}
            placeHolder={"Choose grade"}
            defaultSelectedID={0}
            errors={errors}
            dispatchValue={"gradeCode"}
            register={register}
            disabled={isDisable}
          />
        </div>

        {renderExtraFields && renderExtraFields({ register, errors, watch, setValue, handleInputKeyPress })}

        <div className="col-12 mb-12">
          <label className="tab-lable">School ID</label>
          <input
            type="text"
            style={{ fontFamily: "Source Code Pro" }}
            className={`member--inputbox ${errors.invitationCode ? "error--strike" : "input-highlight"}`}
            placeholder="Enter school ID"
            {...register("invitationCode")}
            required
            disabled={isDisable || isInvitationCodeDisabled}
          />
          {errors.invitationCode && <span className="error--strike--text">{errors.invitationCode.message}</span>}
        </div>

        <div className="position-relative">
          <label className="tab-lable">Phone Number</label>
          <PhoneInput
            form={form}
            disabled={isEditEnabled || loader}
            handleChange={handlePhoneNumberChange}
            onBlur={() => onBlur("phoneNumber")}
            handleEditNumberClick={handleEditNumberClick}
            isEditEnabled={isEditEnabled}
            onCountryChange={handleCountryChange}
          />
          {errors.phoneNumber && <span className="error--strike--text">{errors.phoneNumber.message}</span>}
        </div>

        {showOtpInput && (
          <div className="mt-3">
            <label className="tab-lable">Enter OTP</label>
            <div className="col-12 position-relative mb-12">
              <div className="d-flex align-items-center">
                <OtpInput
                  inputType="number"
                  skipDefaultStyles={false}
                  shouldAutoFocus="false"
                  value={otpValue.join("")}
                  onChange={(e) => handleOtpCodeChange(e)}
                  numInputs={4}
                  separator={<span> </span>}
                  isDisabled={disabled.otpForm}
                  inputStyle="otp-input-box"
                  renderInput={(props, index) => (
                    <input
                      {...props}
                      key={index}
                      required
                      className={`otp-input-box ${errors.otpCode?.message ? "otp-error-strick" : "highlight"}`}
                    />
                  )}
                />
                <div className="resend_otp">
                  {!isTimerCompleted && (
                    <span>
                      Resend OTP <br />({seconds} Sec.)
                    </span>
                  )}
                  {isTimerCompleted && (
                    <button type="button" className="resend-btn" onClick={clickSendOTP}>
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            </div>
            {errors.otpCode && <span className="error--strike--text">{errors.otpCode.message}</span>}
          </div>
        )}
      </div>

      <div className="col-12 mb-3">
        <div className="form-check check-opacity">
          <label className="form-check-label text-muted">
            <input type="checkbox" className="form-check-input" name="remember" {...register("remember")} />
            <i className="input-helper"></i> Notify me on WhatsApp <br />
          </label>
          <br />
          <span className="t-c_class">
            By Signing up, you agree with our{" "}
            <span onClick={toggleTAndC} className="gard--textflow mx-1">
              T&C
            </span>{" "}
            and{" "}
            <span className="gard--textflow mx-1" onClick={togglePAndP}>
              PP
            </span>
          </span>
          <br />
          {errors.remember && <span className="error--strike--text">This field is required</span>}
        </div>
      </div>

      <button
        type="button"
        className="button--membersubmit w-100"
        onClick={handleButtonAction}
        disabled={isButtonDisabled}
      >
        {isOtpSent ? (
          loader ? (
            <div className="loader-container">
              <img className="loader-gif" src={loaderGif} alt="Loading.." />
              <p className="loader-text">Loading</p>
            </div>
          ) : (
            "Create Account"
          )
        ) : loader ? (
          <div className="loader-container">
            <img className="loader-gif" src={loaderGif} alt="Loading.." />
            <p className="loader-text">Loading</p>
          </div>
        ) : (
          "Continue"
        )}
      </button>

      <div className="hr-box">
        <hr className="hr-line" />
        <span>Or</span>
        <hr className="hr-line" />
      </div>

      <div className="col-12 mt-3">
        <p className="login--supporttext">
          Already having account?
          <Link to={ROUTES.STUDENT} className="gard--textflow mx-1">
            Login
          </Link>
        </p>
      </div>
    </form>
  );
};
